import React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Badge } from '@codes/ui-kit/components/ui/badge';
import { Button } from '@codes/ui-kit/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { Image } from '@codes/ui-kit/components/ui/image';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { ProjectView, WorkspaceView } from '@lemoncloud/codes-goods-api';
import { DropdownMenuCheckboxItemProps } from '@radix-ui/react-dropdown-menu';
import { useWorkspaceService } from '@web/app/shared';
import {
    ErrorCard,
    useCreateProjectDialog,
    useCreateWorkspaceDialog,
    useDialogStore,
    useSelectedStore,
    useWorkspaceNavigation,
} from '@web/app/shared';
import { ChevronDown, ChevronsLeft, Clipboard, Home, Plus, User } from 'lucide-react';

import {
    AiLogoDarkImage,
    AiLogoImage,
    BoxBlackSymbol,
    BoxWhiteSymbol,
    LogoDarkImage,
    LogoLightImage,
} from '@codes/assets';
import { useOwnerStore } from '@codes/owner';
import { SharedSpinner, cn } from '@codes/ui-kit';

import { CreateProjectDialog } from '../create-project-dialog';
import { CreateWorkspaceDialog } from '../create-workspace-dialog';
import { EnrollCardDialog } from '../enroll-card-dialog';
import { PricingPlansDialog } from '../pricing-plans-dialog';
import { WorkspacePendingDialog } from '../workspace-pending-dialog';

export type Checked = DropdownMenuCheckboxItemProps['checked'];

interface SideNavProps {
    isSidebarOpen: boolean;
    setIsSidebarOpen: (open: boolean) => void;
}

export const SideNav = ({ isSidebarOpen, setIsSidebarOpen }: SideNavProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname, search } = useLocation();

    const [workspaceMenuOpened, setWorkspaceMenuOpened] = useState(false);
    const [projectMenuOpened, setProjectMenuOpened] = useState(false);
    const [myMenuOpened, setMyMenuOpened] = useState(false);
    const [aiAgentOpened, setAIAgentOpened] = useState(false);

    const { openWorkspacePendingDialog } = useDialogStore();

    const ownerInfo = useOwnerStore(state => state.owner);
    const { workspace, project, setProject } = useSelectedStore();
    const {
        navigateToWorkspace,
        navigateToProject,
        navigateToProjectPath,
        navigateToWorkspacePath,
        navigateToBoxPath,
    } = useWorkspaceNavigation({ workspace, project });

    const { myWorkspacesQueryResult, myWorkspaces, myProjectsQueryResult, myProjects, isApprovedWorkspace } =
        useWorkspaceService();

    const myFullWorkspaces = [...new Map([...myWorkspaces, workspace].map(ws => [ws?.id, ws])).values()].filter(
        workspace => !!workspace
    ) as WorkspaceView[];

    const myFullProjects = [
        ...new Map([...myProjects, project].map(project => [project?.id, project])).values(),
    ].filter(project => !!project) as ProjectView[];

    const handleAddedCompleteToCreateWorkspace = (workspace: WorkspaceView) => {
        navigate(`/w/${workspace.id}`);
        refreshWithChangedWorkspace(workspace.siteId as string);
    };

    const {
        isWorkspaceDialogOpen,
        setWorkspaceDialog,
        handleClose: handleCloseToCreateWorkspaceDialog,
        handleComplete: handleCompleteToCreateWorkspace,
        refresh: refreshWithChangedWorkspace,
    } = useCreateWorkspaceDialog({
        onComplete: handleAddedCompleteToCreateWorkspace,
    });

    const handleAddedCompleteToCreateProject = (_: ProjectView) => {
        toast({
            description: t('project.create.projectCreationCompleted'),
        });
    };
    const {
        isProjectDialogOpen,
        setProjectDialog,
        handleClose: handleCloseToCreateProjectDialog,
        handleComplete: handleCompleteToCreateProject,
    } = useCreateProjectDialog({
        onComplete: handleAddedCompleteToCreateProject,
    });

    const openWorkspaceDialog = () => {
        setWorkspaceDialog(true);
        setWorkspaceMenuOpened(false);
    };

    const handleWorkspaceMenuChange = (open: boolean) => {
        setWorkspaceMenuOpened(open);
    };

    const handleProjectMenuChange = (open: boolean) => {
        setProjectMenuOpened(open);
    };

    const handleAIAgentMenuChange = (open: boolean) => {
        setAIAgentOpened(open);
    };

    const handleMyMenuChange = (open: boolean) => {
        setMyMenuOpened(open);
    };

    const handleLogoClick = () => {
        navigateToWorkspace();
    };

    const handleProjectClick = async (newProject: ProjectView) => {
        setProject(newProject);
        navigateToProject(newProject);
    };

    const getWorkspaceBadge = (stereo?: string) => {
        switch (stereo) {
            case 'free':
            case 'basic':
                return (
                    <Badge variant="C" className="bg-sub">
                        Basic
                    </Badge>
                );
            case 'pro':
                return (
                    <Badge variant="C" className="bg-pro">
                        pro
                    </Badge>
                );
            case 'ent':
                return (
                    <Badge variant="C" className="bg-adv">
                        adv
                    </Badge>
                );
            default:
                return null;
        }
    };

    const openProjectDialog = () => {
        setProjectMenuOpened(false);

        if (!isApprovedWorkspace) {
            openWorkspacePendingDialog();
            return;
        }

        setProjectDialog(true);
    };

    return (
        <div
            className={`bg-background max-sm:hidden fixed top-0 bottom-0 left-0 z-[100] border-r border-border dark:border-[#373839] transition-all duration-300 ease-in-out pt-[13px] px-4 ${
                isSidebarOpen ? 'w-60' : 'w-0 !px-0'
            } overflow-hidden`}
        >
            <div className="mb-[22px] flex items-center justify-between">
                <button onClick={handleLogoClick}>
                    <Image className="h-[29px]" src={LogoLightImage} darkSrc={LogoDarkImage} alt="logo" />
                </button>
                <button onClick={() => setIsSidebarOpen(false)}>
                    <ChevronsLeft className="h-[18px] w-[18px] hover:text-[#BABCC0]" />
                </button>
            </div>
            <div
                className={`flex flex-col gap-2 transition-all duration-300 whitespace-nowrap ${
                    isSidebarOpen ? '' : 'hidden'
                }`}
            >
                <DropdownMenu open={workspaceMenuOpened} onOpenChange={handleWorkspaceMenuChange}>
                    <DropdownMenuTrigger
                        className={cn(
                            'h-[52px] focus-visible:outline-0 border border-transparent flex items-center justify-between py-[15px] px-4 bg-default rounded-lg',
                            workspaceMenuOpened &&
                                'border border-[rgba(0,87,156,0.42)] dark:border-[rgba(0,87,156,0.68)]'
                        )}
                    >
                        {myWorkspacesQueryResult.isLoading ? (
                            <SharedSpinner />
                        ) : (
                            <>
                                <div className="flex items-center gap-2">
                                    <Home className="w-4 h-4" />
                                    <div className="font-medium">{t('common.workspace')}</div>
                                </div>
                                <ChevronDown className={cn('w-5 h-5', workspaceMenuOpened && 'rotate-180')} />
                            </>
                        )}
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[216px] py-4 px-3 border-hover max-h-[400px] overflow-auto">
                        {myWorkspacesQueryResult.isError && (
                            <ErrorCard
                                error={myWorkspacesQueryResult.error}
                                resetError={() => myWorkspacesQueryResult.refetch()}
                            />
                        )}

                        {myFullWorkspaces.map((origin: WorkspaceView) => (
                            <DropdownMenuCheckboxItem
                                key={origin.id}
                                checked={origin?.id === workspace?.id}
                                onCheckedChange={() => {
                                    navigate(`/w/${origin.siteId}`);
                                    refreshWithChangedWorkspace(origin.siteId as string);
                                }}
                            >
                                <div className="flex items-center gap-[6px] hover:text-point">
                                    <div className="max-w-[120px] truncate">{origin.name}</div>
                                    {origin?.stereo && getWorkspaceBadge(origin.stereo)}
                                </div>
                            </DropdownMenuCheckboxItem>
                        ))}

                        <div className="px-4">
                            <Button
                                size="md"
                                className="bg-secondary w-full flex items-center gap-1 mt-[18px]"
                                onClick={openWorkspaceDialog}
                            >
                                <Plus className="w-4 h-4" />
                                {t('workspace.list.newWorkspace')}
                            </Button>
                        </div>
                    </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu open={projectMenuOpened} onOpenChange={handleProjectMenuChange}>
                    <DropdownMenuTrigger
                        className={cn(
                            'h-[52px] focus-visible:outline-0 border border-transparent flex items-center justify-between py-[15px] px-4 bg-default rounded-lg',
                            projectMenuOpened && 'border border-[rgba(0,87,156,0.42)] dark:border-[rgba(0,87,156,0.68)]'
                        )}
                    >
                        {!ownerInfo && <SharedSpinner />}
                        {!!ownerInfo && (
                            <>
                                <div className="flex items-center gap-2">
                                    <Clipboard className="w-4 h-4" />
                                    <div className="font-medium">{t('common.project')}</div>
                                </div>
                                <ChevronDown className={cn('w-5 h-5', projectMenuOpened && 'rotate-180')} />
                            </>
                        )}
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[216px] py-4 px-3 border-hover">
                        {myProjectsQueryResult.isError && (
                            <ErrorCard
                                error={myProjectsQueryResult.error}
                                resetError={() => myProjectsQueryResult.refetch()}
                            />
                        )}
                        {myFullProjects.map((data: ProjectView) => (
                            <DropdownMenuCheckboxItem
                                key={data.id}
                                checked={project?.id === data.id}
                                onCheckedChange={() => handleProjectClick(data)}
                            >
                                {data.name}
                            </DropdownMenuCheckboxItem>
                        ))}
                        <div className="px-4">
                            <Button
                                onClick={openProjectDialog}
                                size="md"
                                className="bg-secondary w-full flex items-center gap-1 mt-[18px]"
                                disabled={!workspace}
                            >
                                <Plus className="w-4 h-4" />
                                {t('workspace.list.newProject')}
                            </Button>
                        </div>
                    </DropdownMenuContent>
                </DropdownMenu>
                {project && (
                    <>
                        <DropdownMenu open={aiAgentOpened} onOpenChange={handleAIAgentMenuChange}>
                            <DropdownMenuTrigger
                                className={cn(
                                    'h-[52px] focus-visible:outline-0 border border-transparent flex items-center justify-between py-[15px] px-4 bg-default rounded-lg',
                                    aiAgentOpened &&
                                        'border border-[rgba(0,87,156,0.42)] dark:border-[rgba(0,87,156,0.68)]'
                                )}
                            >
                                <div className="flex items-center gap-2">
                                    <Image className="w-4 h-4" src={AiLogoImage} darkSrc={AiLogoDarkImage} />
                                    <div className="font-medium">AI</div>
                                </div>
                                <ChevronDown className={cn('w-5 h-5', aiAgentOpened && 'rotate-180')} />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="w-[216px] py-4 px-3 border-hover">
                                <DropdownMenuCheckboxItem
                                    checked={pathname.includes('/compare-chats')}
                                    onCheckedChange={() => navigateToProjectPath('/compare-chats')}
                                >
                                    {t('ai.chat.title')}
                                </DropdownMenuCheckboxItem>
                                <DropdownMenuCheckboxItem
                                    className="hover:text-point"
                                    checked={pathname.includes('/prompts')}
                                    onCheckedChange={() => navigateToProjectPath('/prompts?page=0&stereo=system')}
                                >
                                    {t('prompt.title')}
                                </DropdownMenuCheckboxItem>
                                <DropdownMenuCheckboxItem
                                    checked={pathname.includes('/my-agents')}
                                    onCheckedChange={() => navigateToProjectPath('/my-agents')}
                                >
                                    {t('agent.title')}
                                </DropdownMenuCheckboxItem>
                                <DropdownMenuCheckboxItem
                                    className="hover:text-point"
                                    checked={pathname.includes('/agent-catalogs')}
                                    onCheckedChange={() => navigateToProjectPath('/agent-catalogs')}
                                >
                                    {t('agent.catalogTitle')}
                                </DropdownMenuCheckboxItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <DropdownMenu>
                            <DropdownMenuTrigger
                                className="h-[52px] focus-visible:outline-0 border border-transparent flex items-center justify-between py-[15px] px-4 bg-default rounded-lg hover:border-[rgba(0,87,156,0.42)] dark:hover:border-[rgba(0,87,156,0.68)]"
                                onClick={() => navigateToBoxPath()}
                            >
                                <div className="flex items-center gap-2">
                                    <Image className="w-4 h-4" src={BoxBlackSymbol} darkSrc={BoxWhiteSymbol} />
                                    <div className="font-medium">EurekaBox</div>
                                </div>
                            </DropdownMenuTrigger>
                        </DropdownMenu>
                    </>
                )}
                <DropdownMenu open={myMenuOpened} onOpenChange={handleMyMenuChange}>
                    <DropdownMenuTrigger
                        className={cn(
                            'h-[52px] focus-visible:outline-0 border border-transparent flex items-center justify-between py-[15px] px-4 bg-default rounded-lg',
                            myMenuOpened && 'border border-[rgba(0,87,156,0.42)] dark:border-[rgba(0,87,156,0.68)]'
                        )}
                    >
                        <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <div className="font-medium">MY</div>
                        </div>
                        <ChevronDown className={cn('w-5 h-5', myMenuOpened && 'rotate-180')} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[216px] py-4 px-3 border-hover">
                        <DropdownMenuCheckboxItem
                            checked={pathname.includes('/my-page') && search === '?tab=info'}
                            onCheckedChange={() => navigateToWorkspacePath('/my-page?tab=info')}
                        >
                            {t('header.personalInfo')}
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem
                            checked={pathname.includes('/my-page') && search === '?tab=payment'}
                            onCheckedChange={() => navigateToWorkspacePath('/my-page?tab=payment')}
                        >
                            {t('header.billing')}
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem
                            checked={pathname.includes('/my-page') && search === '?tab=subscriptions'}
                            onCheckedChange={() => navigateToWorkspacePath('/my-page?tab=subscriptions')}
                        >
                            {t('header.subscriptions')}
                        </DropdownMenuCheckboxItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* TODO: 전역 dialog 어디에 선언할지는 추후 개선  */}
            <CreateWorkspaceDialog
                handleClose={handleCloseToCreateWorkspaceDialog}
                handleComplete={handleCompleteToCreateWorkspace}
            />

            <CreateProjectDialog
                handleClose={handleCloseToCreateProjectDialog}
                handleComplete={handleCompleteToCreateProject}
                workspace={workspace as WorkspaceView}
            />

            <PricingPlansDialog />
            <EnrollCardDialog />

            <WorkspacePendingDialog />
        </div>
    );
};
